import { Observable } from 'rxjs';
import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { throwError } from 'rxjs/internal/observable/throwError';
import { of } from 'rxjs/internal/observable/of';
import moment from 'moment/moment';
import { FilterItem, OrderBy, PaginatedResponse } from '../_models/paginatedResponse';
import {
  Crop,
  FieldActivity,
  FieldActivityGrouped,
  FieldActivityItem,
  LastDoneActivityFromFieldResponse,
} from '../_models';
import { environment } from '../../../environments/environment';
import { CropMetricsService } from './cropMetrics.service';
import { FieldActivitiesCustomType } from '../_models/fieldActivityCustomType';
import { FieldActivityTypes } from '../../../assets/data/enum/fieldActivityTypes';
import { ExportConfig } from '../_models/export';
import { UnitMeasurement } from '../../../assets/data/enum/unit_measurement.enum';

@Injectable({ providedIn: 'root' })
export class FieldActivityService {
  private http = inject(HttpClient);
  private cropMetricsService = inject(CropMetricsService);

  private apiBaseUrlV1 = environment.apiBaseUrl + 'api/private/v1';
  private apiBaseUrlV2 = environment.apiBaseUrl + 'api/private/v2';
  private apiActivityServiceUrlV2 = environment.activityServiceUrl + 'api/private/v2/cache';

  private unitMeasurement = UnitMeasurement;
  private lastPagedResult = signal<PaginatedResponse<FieldActivity> | null>(null);
  private lastItemResult = signal<FieldActivity | null>(null);

  lastPagedResultSignal = this.lastPagedResult.asReadonly();
  lastItemResultSignal = this.lastItemResult.asReadonly();

  /**
   * Fetches paginated field activities with optional filters, sorting and search.
   *
   * @param params.number Page number starting from 1!
   * @param params.max Number of items per page
   * @param params.textSearch Free text search
   * @param params.year Filter activities by year
   * @param params.doneFilter Filter activities by done status
   * @param params.filterToApply Additional filters to apply
   * @param params.orderBy Sorting configuration
   * @returns Observable with paginated response
   */
  getFieldActivitiesPaged(params: {
    number?: number;
    max?: number;
    textSearch?: string;
    year?: number;
    doneFilter?: boolean | null;
    filterToApply?: FilterItem[];
    orderBy?: OrderBy[];
  }): Observable<PaginatedResponse<FieldActivity>> {
    const companyId = JSON.parse(localStorage.getItem('companySelected') ?? 'null')?.id ?? null;

    const filtersList: FilterItem[] = [
      ...(params.filterToApply ?? []),
      ...(params.doneFilter != null ? [{ name: 'done', value: params.doneFilter }] : []),
      ...(params.year != null ? [{ name: 'year', value: params.year }] : []),
      { name: 'companyId', value: companyId },
    ];

    const payload = {
      ...(params.number != null && { number: params.number }),
      ...(params.max != null && { max: params.max }),
      ...(params.textSearch?.trim() && { textSearch: params.textSearch.trim() }),
      sort: {
        orderBy: params.orderBy ?? [{ property: 'activityDate', direction: 'DESC' }],
      },
      filter: {
        filters: filtersList,
      },
    };

    return this.http
      .post<
        PaginatedResponse<FieldActivity>
      >(`${this.apiBaseUrlV2}/fieldActivity?excludes=machineActivities`, payload)
      .pipe(
        tap((result) => this.lastPagedResult.set(result)),
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
  }

  getFieldActivityById(id: number): Observable<FieldActivity> {
    const url = `${this.apiBaseUrlV1}/fieldActivities/${id}`;

    return this.http.get<FieldActivity>(url).pipe(
      tap((result) => this.lastItemResult.set(result)),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  deleteFieldActivityById(id: number): Observable<FieldActivity> {
    return this.http.delete<void>(`${this.apiBaseUrlV1}/fieldActivities/${id}`).pipe(
      tap(() => {
        this.cropMetricsService.getCropMetrics().subscribe();
      }),
      map(() => ({ id }) as FieldActivity)
    );
  }

  deleteFieldActivityItemById(id: number): Observable<FieldActivityItem> {
    const url = `${this.apiBaseUrlV1}/fieldActivityItems/${id}`;

    return this.http.delete<void>(url).pipe(map(() => ({ id }) as FieldActivityItem));
  }

  saveFieldActivity(fieldActivityItem: any): Observable<FieldActivity> {
    const fieldActivity = { ...fieldActivityItem };
    delete fieldActivity.lastUpdated;
    delete fieldActivity.dateCreated;
    delete fieldActivity.cropObj;
    delete fieldActivity.verificationDate;
    delete fieldActivity.machineActivities;

    // Normalize mixes
    if (Array.isArray(fieldActivity.mixes)) {
      fieldActivity.mixes = fieldActivity.mixes.map((mix) => ({
        ...mix,
        product: mix.product ? { id: mix.product.id } : mix.product,
      }));
    }

    // Normalize equipments
    if (Array.isArray(fieldActivity.equipments)) {
      fieldActivity.equipments = fieldActivity.equipments.map((equipment) => ({
        id: equipment.id,
      }));
    }

    // Normalize users
    if (Array.isArray(fieldActivity.users)) {
      fieldActivity.users = fieldActivity.users.map((user) => ({
        id: user.id,
      }));
    }

    // Normalize field
    if (fieldActivity.field) {
      fieldActivity.field = { id: fieldActivity.field.id };
    }

    // Attach companyId
    const companySelected = JSON.parse(localStorage.getItem('companySelected') ?? 'null');
    fieldActivity.companyId = companySelected?.id ?? null;

    // Choose PUT or POST based on the presence of the entity id
    const request$ = fieldActivity.id
      ? this.http.put<FieldActivity>(
          `${this.apiBaseUrlV1}/fieldActivities/${fieldActivity.id}`,
          fieldActivity
        )
      : this.http.post<FieldActivity>(`${this.apiBaseUrlV1}/fieldActivities/`, fieldActivity);
    return request$.pipe(
      // Trigger a side effect after a successful save without altering the stream
      tap(() => {
        this.cropMetricsService.getCropMetrics();
      })
    );
  }

  getFieldActivitiesCustomType() {
    return this.http
      .get<FieldActivitiesCustomType[]>(`${this.apiBaseUrlV1}/fieldActivitiesCustomType`)
      .pipe(
        map((response) =>
          (response ?? []).slice().sort((a, b) => a.customType.localeCompare(b.customType))
        )
      );
  }

  saveFieldActivitiesCustomType(params: {
    companyId: number;
    customType: string;
  }): Observable<any> {
    return this.http.post<FieldActivitiesCustomType>(
      `${this.apiBaseUrlV1}/fieldActivitiesCustomType`,
      params
    );
  }

  deleteFieldActivityCustomType(
    fieldActivityCustomType: FieldActivitiesCustomType
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiBaseUrlV1}/fieldActivitiesCustomType/${fieldActivityCustomType.id}`
    );
  }

  getMostRecentActivityTypes(): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.apiBaseUrlV1}/activities/field/mostRecentTypes`)
      .pipe(catchError(() => of([])));
  }

  getFieldActivityExpandedById(id: number | string): Observable<FieldActivityGrouped> {
    return this.http.get<FieldActivity>(`${this.apiBaseUrlV1}/fieldActivities/${id}`).pipe(
      map((response) => {
        const grouped = new FieldActivityGrouped();

        grouped.uuid = response.uuid;
        grouped.equipmentActivities = response.machineActivities;
        grouped.activities = [response];

        return grouped;
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  getFieldActivitiesOPModule(
    farmsAppend: string,
    selectedYear: number
  ): Observable<FieldActivity[]> {
    const excludesList = ['machineActivities', 'equipmentActivities'];

    const searchParams = new URLSearchParams(farmsAppend);

    excludesList.forEach((e) => {
      searchParams.append('excludes', e);
    });

    searchParams.set('cropYear', '' + selectedYear);

    return this.http
      .get<
        FieldActivity[]
      >(`${this.apiActivityServiceUrlV2}/fieldActivities?${searchParams.toString()}`)
      .pipe(map((response) => response));
  }

  checkFieldActivitiesByCropId(crop: Crop): Observable<any> {
    if (!crop) {
      return this.http.get<any>(`${this.apiBaseUrlV1}/fieldActivities`);
    }

    const params: Record<string, string> = {};

    if (crop.uuid) {
      params['cropUuid'] = crop.uuid;
    } else if (crop.id) {
      params['cropId'] = String(crop.id);
    }

    return this.http.get<any>(`${this.apiBaseUrlV1}/fieldActivities`, {
      params,
    });
  }

  /**
   * 🧩Internal helper methods
   */

  getUnitMeasurementConfiguration() {
    return [this.unitMeasurement.KG_HA, this.unitMeasurement.L_HA, this.unitMeasurement.UNIT];
  }

  getUnitMeasurementConfigurationSeeding() {
    return [this.unitMeasurement.KG_HA, this.unitMeasurement.UNIT];
  }

  getUnitMeasurementConfigurationIrrigating() {
    return [
      this.unitMeasurement.L_S,
      this.unitMeasurement.MQ_H,
      this.unitMeasurement.L_MIN,
      this.unitMeasurement.L_H,
      this.unitMeasurement.MM,
    ];
  }

  getUnitMeasurementConfigurationFieldSubmersion() {
    return [this.unitMeasurement.L, this.unitMeasurement.MC];
  }

  getUnitMeasurementConfigurationDeposit(): string[] {
    return [this.unitMeasurement.KG, this.unitMeasurement.L, this.unitMeasurement.BU];
  }

  getExportType(): ExportConfig[] {
    return [
      {
        type: 'FieldActivityList',
        subtype: 'FieldActivityList',
        formats: [
          {
            type: 'csv',
            enabled: true,
            compression: false,
            col: '6',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '6',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'WorkPlan',
        formats: [
          {
            type: 'xlsx',
            enabled: true,
            compression: false,
            col: '12',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'CultivationPlan',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'Treatments',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
      {
        type: 'FieldActivities',
        subtype: 'PlotsAndParcels',
        formats: [
          {
            type: 'csv',
            enabled: false,
            compression: true,
            col: '4',
          },
          {
            type: 'xls',
            enabled: true,
            compression: false,
            col: '4',
          },
          {
            type: 'pdf',
            enabled: true,
            compression: false,
            col: '4',
          },
        ],
      },
    ];
  }

  newFilterForFieldActivities(
    fieldActivities: FieldActivity[],
    startDate: number,
    endDate: number,
    userIds: number[],
    fieldIds?: number[]
  ): FieldActivity[] {
    const hasUserFilter = userIds?.length > 0;
    const hasFieldFilter = fieldIds?.length > 0;
    const hasDateFilter = startDate > 0 && endDate > 0;

    const filtered = fieldActivities.filter((activity) => {
      if (hasUserFilter) {
        const hasMatchingUser = activity.users?.some((user) => userIds.includes(user.id));
        if (!hasMatchingUser) {
          return false;
        }
      }

      if (hasDateFilter) {
        const activityDay = moment(activity.activityDate).startOf('day').valueOf();
        if (
          activityDay < moment(startDate).startOf('day').valueOf() ||
          activityDay > moment(endDate).endOf('day').valueOf()
        ) {
          return false;
        }
      }

      if (hasFieldFilter) {
        return fieldIds!.includes(activity.field.id);
      }

      return true;
    });

    return Array.from(new Set(filtered));
  }

  getAllFieldActivityTypes = (): string[] => {
    const keys = this.getAllKeys(FieldActivityTypes).sort((a, b) => a.localeCompare(b));

    return keys.map((key) => (key.includes('.') ? key.split('.')[1] : key));
  };

  private getAllKeys(obj: any): string[] {
    let keys: string[] = [];

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        keys.push(key);
        if (typeof obj[key] === 'object' && Object.keys(obj[key]).length > 0) {
          const subKeys = this.getAllKeys(obj[key]);
          keys = keys.concat(subKeys.map((subKey) => `${key}.${subKey}`));
        }
      }
    }

    return keys;
  }

  getLastDoneActivityFromField(cropYear: number) {
    return this.http
      .get<LastDoneActivityFromFieldResponse>(
        `${this.apiBaseUrlV2}/fieldActivity/retrieveLastDoneActivityFromField?cropYear=${cropYear}`
      )
      .pipe(map((response) => response?.content ?? []));
  }
}
